import React, { useCallback, useEffect, useState } from 'react';
import { Layers } from 'lucide-react';
import Button from '../ui/Button';
import Drawer from '../ui/Drawer';
import ConfirmDialog from '../ui/ConfirmDialog';
import { apiFetch } from '../../services/apiClient';
import { positionList } from '../../services/positions';
import { numberLabel } from '../../services/ranks';
import AddNewButton from '../ui/AddNewButton';
import ItemCard, { CARD_GRID, CardSection, Chip } from '../ui/ItemCard';

// =============================================================================
// Positions -- the department's job titles (Oct 2026, src/logic/positions.py).
// =============================================================================
// Display only. Each carries a SUGGESTED rank and on-call grade that fill in
// when the position is picked for somebody; the person keeps their own numbers
// and the solver reads only those. Renaming a position renames it for everyone
// who holds it (it is stored by key). A position somebody holds cannot be
// removed until they have another.
//
// One card per position, edited in a side panel, added from the dashed button
// at the bottom -- the same as every other list (Oct 2026).
// =============================================================================

const label = 'text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] ml-1';
const box = 'w-full h-[44px] px-4 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)]';

// A stable fallback: a new `() => {}` per render would re-run the load effect
// (it depends on onBanner) on every render, overwriting fresh edits.
const NO_BANNER = () => {};

/** Add or edit one position. `position` null = a new one. */
function PositionDrawer({ position, onClose, onSave, saving, rankLabels, gradeLabels }) {
  const [name, setName] = useState(position?.label || '');
  const [rank, setRank] = useState(position ? position.rank : 4);
  const [grade, setGrade] = useState(position ? position.oncallGrade : null);
  const submit = () => onSave({ label: name.trim(), rank, oncall_grade: grade });
  return (
    <Drawer isOpen onClose={onClose} title={position ? 'Edit Position' : 'New Position'}
      footer={(
        <div className="flex gap-3">
          <Button variant="secondary" onClick={onClose} disabled={saving} className="flex-1">Cancel</Button>
          <Button onClick={submit} isLoading={saving} disabled={!name.trim()} className="flex-1">
            {position ? 'Save' : 'Add position'}
          </Button>
        </div>
      )}>
      <div className="space-y-6">
        <div className="flex flex-col gap-1.5">
          <label className={label} htmlFor="position-name">Name</label>
          <input id="position-name" aria-label="Position name" autoFocus value={name}
            onChange={(e) => setName(e.target.value)} placeholder="e.g. Senior Staff Nurse" className={box}
            onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) submit(); }} />
          {position && (
            <p className="text-[11px] text-slate-400 ml-1">
              Renaming changes it for everyone who holds it ({position.holders}).
            </p>
          )}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <label className={label} htmlFor="position-rank">Suggested rank</label>
            <select id="position-rank" aria-label="Suggested rank" value={rank}
              onChange={(e) => setRank(Number(e.target.value))} className={`${box} cursor-pointer`}>
              {[0, 1, 2, 3, 4].map(r => <option key={r} value={r}>{numberLabel(r, rankLabels)}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className={label} htmlFor="position-grade">Suggested on-call grade</label>
            <select id="position-grade" aria-label="Suggested on-call grade" value={grade === null ? '' : grade}
              onChange={(e) => setGrade(e.target.value === '' ? null : Number(e.target.value))} className={`${box} cursor-pointer`}>
              <option value="">No on-call</option>
              {[0, 1, 2].map(g => <option key={g} value={g}>{numberLabel(g, gradeLabels)}</option>)}
            </select>
          </div>
        </div>
        <p className="text-xs text-slate-500 leading-relaxed">
          These fill in when the position is picked for somebody. Each person keeps their own rank and
          grade, and that is what rostering uses — changing a suggestion moves nobody.
        </p>
      </div>
    </Drawer>
  );
}

const PositionsCard = ({ rankLabels = {}, gradeLabels = {}, onBanner = NO_BANNER }) => {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);       // null | { position: row | null }
  const [removing, setRemoving] = useState(null);     // a row
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch('/api/positions');
      setRows(positionList((await res.json()).positions));
    } catch {
      onBanner({ type: 'error', message: 'Could not load the positions.' });
    }
  }, [onBanner]);
  useEffect(() => { load(); }, [load]);

  const call = async (url, method, body, done) => {
    setBusy(true);
    try {
      const res = await apiFetch(url, {
        method, headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      const data = await res.json();
      if (!res.ok) { onBanner({ type: 'error', message: data.detail || 'Could not save the position.' }); return; }
      setRows(positionList(data.positions));
      onBanner({ type: 'success', message: 'Positions saved.' });
      done();
    } catch {
      onBanner({ type: 'error', message: 'Request failed — is the backend running?' });
    } finally {
      setBusy(false);
    }
  };

  const save = (body) => {
    const position = editing?.position;
    call(position ? `/api/positions/${encodeURIComponent(position.key)}` : '/api/positions',
      position ? 'PUT' : 'POST', body, () => setEditing(null));
  };

  return (
    <div className="space-y-4">
      <div className="ml-1">
        <h3 className="text-sm font-black text-slate-900 tracking-tight">Positions</h3>
        <p className="text-xs text-slate-500 mt-1 font-medium leading-relaxed max-w-2xl">
          Your department&apos;s job titles, picked for each person in their staff record and shown on
          rosters and exports. A position&apos;s rank and on-call grade are only suggestions that fill in
          when it is picked.
        </p>
      </div>

      <div className={CARD_GRID}>
        {rows.map(row => (
          <ItemCard key={row.key} title={row.label}
            onEdit={() => setEditing({ position: row })}
            onDelete={() => setRemoving(row)}
            deleteDisabledReason={row.holders > 0 ? 'Held by somebody — give them another position first' : undefined}
            footerLeft={`Held by ${row.holders}`}>
            <CardSection icon={Layers} label="Suggests">
              <div className="flex flex-wrap gap-1.5">
                <Chip>{`Rank ${numberLabel(row.rank, rankLabels)}`}</Chip>
                <Chip tone="teal">{row.oncallGrade === null ? 'No on-call' : `On-call ${numberLabel(row.oncallGrade, gradeLabels)}`}</Chip>
              </div>
            </CardSection>
          </ItemCard>
        ))}
      </div>

      <AddNewButton label="New position" onClick={() => setEditing({ position: null })} />

      {editing && (
        <PositionDrawer position={editing.position} saving={busy} onClose={() => setEditing(null)}
          onSave={save} rankLabels={rankLabels} gradeLabels={gradeLabels} />
      )}
      <ConfirmDialog isOpen={Boolean(removing)} onClose={() => setRemoving(null)} tone="danger"
        title={`Remove ${removing?.label || 'this position'}?`}
        body="Nobody holds it, so nothing else changes."
        confirmLabel="Remove" isLoading={busy}
        onConfirm={() => call(`/api/positions/${encodeURIComponent(removing.key)}`, 'DELETE', null, () => setRemoving(null))} />
    </div>
  );
};

export default PositionsCard;
